"use client";

import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function DesktopNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-4">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-6 w-20 animate-pulse rounded-md bg-foreground/10" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-2">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group inline-flex h-9 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-sm font-default font-medium text-foreground transition-colors hover:bg-transparent hover:text-primary focus:bg-transparent focus:text-primary focus:outline-none disabled:pointer-events-none disabled:opacity-50 relative after:absolute after:bottom-1 after:left-4 after:right-4 after:h-[2px] after:scale-x-0 after:bg-primary after:transition-transform hover:after:scale-x-100">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNav({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (!isOpen) return null;
  return <div className="absolute top-full left-0 w-full bg-background border-t border-border shadow-xl md:hidden flex flex-col py-4 px-4 gap-4 z-50">
      {loading ? <div className="flex flex-col gap-4">
          {[1, 2, 3].map((_, i) => <div key={i} data-index={i} className="h-10 w-full animate-pulse rounded-md bg-foreground/5" />)}
        </div> : <nav className="flex flex-col gap-2">
          {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} onClick={onClose} className="flex items-center py-3 px-4 rounded-md text-base font-default font-medium text-foreground hover:bg-primary/5 hover:text-primary transition-colors">
              {item.label}
            </Link>)}
        </nav>}
      
      <div className="flex flex-col gap-4 pt-4 border-t border-border mt-2">
        <Button asChild size="lg" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg font-default font-semibold">
          <Link to="https://go.kycu.ac.kr/bbs/jws_reg00.php" newTab>2026학년도 지원서 작성</Link>
        </Button>
      </div>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (hashPart === '') {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            return;
          }
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <section data-section-id="252290" id="헤더" className={cn("text-foreground sticky top-0 z-50 w-full transition-all duration-300 border-b", isScrolled ? "bg-background/80 backdrop-blur-md border-border shadow-md" : "bg-background border-transparent")}>
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <div className="flex h-20 items-center justify-between gap-4">
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center" aria-label="JOYBOT GEO">
              <WvcLogo className="h-10 w-auto" />
            </Link>
          </div>

          <div className="flex-1 flex justify-center">
            <MenuProvider menu_id="primary">
              <DesktopNav />
            </MenuProvider>
          </div>

          <div className="hidden lg:flex items-center gap-6 flex-shrink-0">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20 font-default font-semibold rounded-lg">
              <Link to="https://go.kycu.ac.kr/bbs/jws_reg00.php" newTab>2026학년도 지원서 작성</Link>
            </Button>
          </div>

          <div className="flex lg:hidden items-center gap-4">
            <Button variant="ghost" size="icon" className="text-foreground hover:bg-secondary hover:text-primary" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
              {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
            </Button>
          </div>
        </div>
      </div>

      <MenuProvider menu_id="primary">
        <MobileNav isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
      </MenuProvider>
    </section>;
}